import { useEffect, useState } from 'react'
import { HaloUserCombobox } from '@/features/halocrm-user/HaloUserCombobox'
import { useHaloUser } from '@/features/halocrm-user/hooks'
import { TeamCombobox } from './TeamCombobox'
import type { AssigneeInput } from '../types'

interface AssigneePickerProps {
  value: AssigneeInput | null
  onChange: (v: AssigneeInput) => void
}

type Mode = 'USER' | 'GROUP'

/**
 * Wybór assignee dla taska - osoba albo zespół.
 *
 * Polityka delegacji 2026-05-28: user dowolny, team dowolny (klient: "wszystkie
 * zespoły dla każdego"). Wcześniej F2A miało "team tylko swój" - poluzowane.
 * Cross-team restriction ewentualnie przez permission `tasks.assign.cross-team`
 * w osobnym specie (Obsidian Follow-ups/Task edit permissions).
 */
export function AssigneePicker({ value, onChange }: AssigneePickerProps) {
  const [mode, setMode] = useState<Mode>(value?.type ?? 'USER')
  const [userId, setUserId] = useState<number | null>(
    value?.type === 'USER' ? value.userId : null,
  )

  const selectedUser = useHaloUser(userId)

  // Gdy user select dostarczy fullName, emituj AssigneeInput w górę.
  // Effect, nie callback - useHaloUser jest asynchroniczny, fullName przychodzi po fetchu.
  useEffect(() => {
    if (mode !== 'USER') return
    if (userId == null) return
    if (!selectedUser.data) return
    onChange({
      type: 'USER',
      userId,
      userName: selectedUser.data.fullName,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, userId, selectedUser.data?.id, selectedUser.data?.fullName])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={mode === 'USER'}
            onChange={() => setMode('USER')}
          />
          Osoba
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={mode === 'GROUP'}
            onChange={() => setMode('GROUP')}
          />
          Zespół
        </label>
      </div>

      {mode === 'USER' && (
        <HaloUserCombobox
          value={userId}
          onChange={(id) => setUserId(id)}
          placeholder="Wybierz osobę..."
        />
      )}

      {mode === 'GROUP' && (
        <TeamCombobox
          value={value?.type === 'GROUP' ? value.group.id : null}
          onChange={(team) => {
            if (team) {
              onChange({
                type: 'GROUP',
                group: { system: 'EASY', id: team.id, label: team.name },
              })
            }
          }}
        />
      )}
    </div>
  )
}
